import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React from 'react';
import { DangerLevel } from '../../types/commands';
import { DangerLevelBadge } from './DangerLevelBadge';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  dangerLevel: DangerLevel;
  isExecuting?: boolean;
}

const dangerMessages: Record<DangerLevel, string> = {
  low: 'This command has minimal impact on the system.',
  medium: 'This command may affect system operations. Please review before proceeding.',
  high: 'This command will significantly impact the system. Proceed with caution.',
  critical:
    'WARNING: This command performs critical operations that could disrupt services. Confirm you understand the consequences.',
};

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  dangerLevel,
  isExecuting = false,
}) => {
  const getDangerAlertSeverity = (level: DangerLevel): 'info' | 'warning' | 'error' => {
    if (level === 'low') return 'info';
    if (level === 'medium') return 'warning';
    return 'error';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {title}
          <DangerLevelBadge level={dangerLevel} size="small" />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {message}
        </Typography>
        <Alert severity={getDangerAlertSeverity(dangerLevel)} sx={{ marginTop: 2 }}>
          {dangerMessages[dangerLevel]}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isExecuting}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={dangerLevel === 'critical' || dangerLevel === 'high' ? 'error' : 'primary'}
          disabled={isExecuting}
        >
          {isExecuting ? 'Executing...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
